'use client'

import type { Entry, TopicRef } from '@/document'
import type { MovieStatus } from './meta'
import { cn } from '@/lib/tailwind'
import {
  FilmSlateIcon,
  CheckIcon,
  ClockIcon,
  StarIcon,
} from '@phosphor-icons/react'
import Image from 'next/image'

interface MovieCardProps {
  movie: Entry & { topics?: TopicRef[] }
  className?: string
}

export function MovieCard({ movie, className }: MovieCardProps) {
  const metadata = movie.metadata as {
    posterUrl?: string
    director?: string
    year?: number
    status?: MovieStatus
    rating?: number
  }
  const topics = movie.topics || []
  const hasPoster = !!movie.imageUrl

  return (
    <div
      className={cn(
        'group hover:border-foreground/20 relative overflow-hidden rounded-lg border transition-colors',
        className,
      )}
    >
      {/* Poster / Placeholder */}
      <div className="bg-muted relative aspect-[2/3] overflow-hidden">
        {hasPoster ? (
          <Image
            src={movie.imageUrl!}
            alt={movie.title}
            fill
            className="object-cover transition-transform group-hover:scale-105"
            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            <FilmSlateIcon className="text-muted-foreground/30 h-12 w-12" />
          </div>
        )}

        {/* Rating badge (subtle, monochrome) */}
        {metadata.rating && (
          <div className="absolute bottom-2 left-2">
            <span className="flex items-center gap-0.5 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white/90 backdrop-blur-sm">
              <StarIcon className="h-2.5 w-2.5" weight="fill" />
              {metadata.rating}
            </span>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="space-y-0.5 p-2.5">
        <div className="flex items-start justify-between gap-1.5">
          <h3 className="line-clamp-2 text-sm leading-tight font-medium">
            {movie.title}
          </h3>
          {/* Subtle status icon */}
          {metadata.status === 'seen' ? (
            <CheckIcon
              className="text-muted-foreground mt-0.5 h-3 w-3 shrink-0"
              weight="bold"
            />
          ) : (
            <ClockIcon className="text-muted-foreground/60 mt-0.5 h-3 w-3 shrink-0" />
          )}
        </div>
        <div className="text-muted-foreground flex items-center gap-1 text-[11px]">
          {metadata.director && (
            <span className="truncate">{metadata.director}</span>
          )}
          {metadata.director && metadata.year && <span>·</span>}
          {metadata.year && <span>{metadata.year}</span>}
        </div>
        {/* Topics instead of genres */}
        {topics.length > 0 && (
          <p className="text-muted-foreground/60 line-clamp-1 text-[11px]">
            {topics
              .slice(0, 3)
              .map((t) => t.name)
              .join(' · ')}
          </p>
        )}
      </div>
    </div>
  )
}
